import { Spinner } from '@fluentui/react-components';
import { getCoprincipalMailboxInfoForMailboxInfo } from 'owa-account-source-list-store';
import { logCoreUsage } from 'owa-analytics';
import { getAccountKeyForMailboxInfo } from 'owa-client-types';
import { DrawerPane, isDrawerPaneVisible } from 'owa-drawer';
import { getIsDarkTheme } from 'owa-fabric-theme';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { getRewardsAccountKey, markRewardsPanelOpened } from 'owa-header-rewards-medallion-store';
import type { OwaHeaderPaneProps } from 'owa-header-types';
import { OWAIframe } from 'owa-iframe';
import loc, { getCurrentLanguageFromCurrentLocale } from 'owa-localize';
import { observer } from 'owa-mobx-react';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import { useState } from 'owa-react';
import { useCallback, useEffect, useRef, type SyntheticEvent, type FunctionComponent } from 'react';
import RewardsFallback from './RewardsFallback';
import getRewardsExpectedAnid from '../utils/getRewardsExpectedAnid';
import { useRewardsCacheSafeWindow } from '../utils/useRewardsCacheSafeWindow';
import waitForFlyoutReady from '../utils/waitForFlyoutReady';
import {
    loadingRewardsText,
    microsoftRewardsTitle,
    rewardsPaneHeaderText,
} from './RewardsPane.locstring.json';
import {
    header,
    headerTitle,
    iframeContainer,
    loadingCover,
    logo,
    titleText,
    bodyContainer,
} from './RewardsPane.scss';

const getRewardsIframeUrl = (isDarkMode: boolean, owaAnid: string) => {
    let returnUrl = `https://www.bing.com/rewards/panelflyout?partnerId=OutlookOwa&isDarkMode=${
        isDarkMode ? 1 : 0
    }&lat=1&lang=${encodeURIComponent(getCurrentLanguageFromCurrentLocale())}`;
    // unresolved we omit it; the server treats a missing value as a mismatch and fails safe.
    if (owaAnid) {
        returnUrl += `&owaAnid=${encodeURIComponent(owaAnid)}`;
    }
    return `https://www.bing.com/fd/auth/signin?action=interactive&provider=windows_live_id&return_url=${encodeURIComponent(
        returnUrl
    )}`;
};
const rewardsIframeStyle = { border: 0, display: 'block', height: '100%', width: '100%' };

function logRewardsPanelClosed(openTimestamp: number, closedWhileLoading: boolean) {
    const durationMs = Math.round(performance.now() - openTimestamp);
    const moduleMailboxInfo = getModuleContextMailboxInfo();
    const telemetryMailboxInfo =
        getCoprincipalMailboxInfoForMailboxInfo(moduleMailboxInfo) ?? moduleMailboxInfo;
    logCoreUsage(
        'RewardsPanelClosed',
        telemetryMailboxInfo,
        { durationMs, closedWhileLoading },
        {}
    );
}

const rewardsPaneTitle = () => {
    return (
        <span className={headerTitle}>
            <svg
                className={logo}
                width="16"
                height="16"
                viewBox="0 0 21 21"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
            >
                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
            </svg>
            <span className={titleText}>{loc(microsoftRewardsTitle)}</span>
        </span>
    );
};

const defaultExport: FunctionComponent<OwaHeaderPaneProps> = observer(function RewardsPane(
    props: OwaHeaderPaneProps
) {
    const isVisible = isDrawerPaneVisible(props.paneId);
    // 'loading' while we resolve the OWA ANID and wait for the flyout's ready signal, 'ready' once
    // the flyout reports a matching identity, 'blocked' if the signal never arrives (cookies
    // blocked), 'mismatch' if the flyout reports the OWA account does not match the Bing session,
    // and 'error' if the flyout loaded but the Rewards platform reported a backend error.
    const [status, setStatus] = useState<'loading' | 'ready' | 'blocked' | 'mismatch' | 'error'>(
        'loading'
    );
    const statusRef = useRef(status);
    if (isVisible) {
        statusRef.current = status;
    }
    // The flyout iframe URL, built with the OWA account's ANID once it resolves. Undefined until
    // then so the iframe is never constructed (and the wrong account never loads) before the
    // identity is known.
    const [iframeUrl, setIframeUrl] = useState<string | undefined>(undefined);

    // Holds the Rewards iframe's contentWindow once it loads, so detection can verify the
    // 'ready' postMessage actually came from our iframe and not another same-origin frame.
    const flyoutWindowRef = useRef<Window | null>(null);
    const handleIframeLoad = useCallback((event: SyntheticEvent<HTMLIFrameElement>) => {
        flyoutWindowRef.current = event.currentTarget.contentWindow;
    }, []);

    // TEMPORARY OWA-only workaround: when the Rewards feature flight is on, the flyout iframe
    // is cached (mounted-but-hidden on dismiss) so reopens are instant. The safe-window guard
    // below forces a reload (re-arming Bing's ANID identity check) whenever a cross-tab cookie
    // swap could have happened; otherwise today's tear-down-on-dismiss behavior is preserved.
    const isRewardsCachingEnabled = isGlobalFeatureEnabled('growth-owaRewardsBadge');
    const { isCacheTrusted, markLoaded } = useRewardsCacheSafeWindow();

    // Stable key of the active OWA coprincipal account, used to invalidate the warm cache when
    // the user switches accounts in-tab (no focus/visibility event fires, but the ANID changes).
    const getCurrentIdentityKey = useCallback(() => {
        const moduleMailboxInfo = getModuleContextMailboxInfo();
        const coprincipalMailboxInfo = getCoprincipalMailboxInfoForMailboxInfo(moduleMailboxInfo);
        return getAccountKeyForMailboxInfo(coprincipalMailboxInfo ?? moduleMailboxInfo);
    }, []);

    // Tracks whether the current open already emitted RewardsPaneOpened. A trusted cached reveal
    // never runs the loading effect (the usual emit site), so we log it from the arming effect
    // instead; reset on dismiss so the next open is counted.
    const hasLoggedOpenRef = useRef(false);
    const openTimestampRef = useRef<number | undefined>(undefined);

    useEffect(() => {
        if (isVisible) {
            const accountKey = getRewardsAccountKey(getModuleContextMailboxInfo());
            markRewardsPanelOpened(accountKey);
        }
    }, [isVisible]);

    useEffect(() => {
        if (isVisible && openTimestampRef.current === undefined) {
            openTimestampRef.current = performance.now();
        } else if (!isVisible && openTimestampRef.current !== undefined) {
            logRewardsPanelClosed(openTimestampRef.current, statusRef.current === 'loading');
            openTimestampRef.current = undefined;
        }
    }, [isVisible]);

    useEffect(
        () => () => {
            if (openTimestampRef.current !== undefined) {
                logRewardsPanelClosed(openTimestampRef.current, statusRef.current === 'loading');
                openTimestampRef.current = undefined;
            }
        },
        []
    );

    // The pane stays mounted while dismissed (preserveContentOnDismiss). Reset on dismiss so the
    // identity guard re-validates on the next open; otherwise a Bing-session change while dismissed
    // could expose the retained iframe unguarded. When caching is on and the flyout is 'ready', we
    // instead keep the warm iframe and defer the decision to the safe-window guard on next open.
    const [prevIsVisible, setPrevIsVisible] = useState(false);
    if (prevIsVisible !== isVisible) {
        setPrevIsVisible(isVisible);
        if (!isVisible) {
            hasLoggedOpenRef.current = false;
            // Dismiss: keep the warm iframe only when caching is on and it was showing content.
            if (!(isRewardsCachingEnabled && status === 'ready')) {
                setStatus('loading');
                setIframeUrl(undefined);
            }
        } else if (
            isRewardsCachingEnabled &&
            status === 'ready' &&
            iframeUrl !== undefined &&
            !isCacheTrusted(getCurrentIdentityKey())
        ) {
            // Re-open with a stale cache (tab lost focus, account switched, or TTL expired): force
            // a reload so Bing re-arms its ANID guard. A trusted cache falls through and reveals
            // instantly.
            setStatus('loading');
            setIframeUrl(undefined);
        }
    }

    // Arm/re-arm the safe window whenever the flyout is shown and ready, so the TTL and
    // focus-loss tracking start from the moment the user is looking at trusted content. A trusted
    // cached reveal skips the loading effect, so emit its pane-open telemetry here (once).
    useEffect(() => {
        if (isRewardsCachingEnabled && isVisible && status === 'ready') {
            if (!hasLoggedOpenRef.current) {
                hasLoggedOpenRef.current = true;
                const moduleMailboxInfo = getModuleContextMailboxInfo();
                const coprincipalMailboxInfo =
                    getCoprincipalMailboxInfoForMailboxInfo(moduleMailboxInfo);
                logCoreUsage(
                    'RewardsPaneOpened',
                    coprincipalMailboxInfo ?? moduleMailboxInfo,
                    { outcome: 'flyout' },
                    { isScorecardEvent: true }
                );
            }
            markLoaded(getCurrentIdentityKey());
        }
    }, [isRewardsCachingEnabled, isVisible, status, markLoaded, getCurrentIdentityKey]);

    useEffect(() => {
        if (!isVisible || status !== 'loading') {
            return;
        }
        // Drop any contentWindow retained from a previous open before re-validating.
        flyoutWindowRef.current = null;
        let cancelled = false;
        const moduleMailboxInfo = getModuleContextMailboxInfo();
        const coprincipalMailboxInfo = getCoprincipalMailboxInfoForMailboxInfo(moduleMailboxInfo);
        const telemetryMailboxInfo = coprincipalMailboxInfo ?? moduleMailboxInfo;
        const isDarkMode = getIsDarkTheme();
        void (async () => {
            const owaAnid = coprincipalMailboxInfo
                ? await getRewardsExpectedAnid(coprincipalMailboxInfo)
                : '';
            if (cancelled) {
                return;
            }
            const readyPromise = waitForFlyoutReady(() => flyoutWindowRef.current);
            setIframeUrl(getRewardsIframeUrl(isDarkMode, owaAnid));
            const flyoutResult = await readyPromise;
            if (cancelled) {
                return;
            }
            let nextStatus: 'ready' | 'blocked' | 'mismatch' | 'error';
            let outcome: 'flyout' | 'blocked' | 'mismatch' | 'error';
            if (flyoutResult.blocked) {
                nextStatus = 'blocked';
                outcome = 'blocked';
            } else if (flyoutResult.anidMatch !== true) {
                nextStatus = 'mismatch';
                outcome = 'mismatch';
            } else if (flyoutResult.flyoutError) {
                nextStatus = 'error';
                outcome = 'error';
            } else {
                nextStatus = 'ready';
                outcome = 'flyout';
            }
            logCoreUsage(
                'RewardsPaneOpened',
                telemetryMailboxInfo,
                { outcome },
                { isScorecardEvent: true }
            );
            hasLoggedOpenRef.current = true;
            setStatus(nextStatus);
        })();
        return () => {
            cancelled = true;
        };
    }, [isVisible, status]);

    return (
        <DrawerPane
            paneId={props.paneId}
            bodyClassName={bodyContainer}
            headerText={rewardsPaneTitle()}
            headerClassName={header}
            noPadding={true}
            preserveContentOnDismiss={isRewardsCachingEnabled}
            role={'complementary'}
            width={380}
        >
            {status === 'blocked' ? (
                <RewardsFallback variant="blocked" />
            ) : status === 'mismatch' ? (
                <RewardsFallback variant="mismatch" />
            ) : status === 'error' ? (
                <RewardsFallback variant="error" />
            ) : (
                <div className={iframeContainer}>
                    {iframeUrl !== undefined && (
                        <OWAIframe
                            isVisible={isVisible}
                            scenario="RewardsPane"
                            src={iframeUrl}
                            title={loc(rewardsPaneHeaderText)}
                            style={rewardsIframeStyle}
                            onLoad={handleIframeLoad}
                            // Least-privilege sandbox: the flyout loads bing.com's sign-in flow,
                            // which needs scripts, same-origin (to read its own bing.com cookies),
                            // forms, and popups for sign-in. These tokens are the minimum the
                            // bing.com sign-in + flyout need; trim further if a functional test
                            // shows any are unnecessary.
                            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
                            // Send only the origin (not the full OWA URL/query) as the Referer to
                            // bing.com on cross-origin requests, to avoid leaking OWA URL details.
                            referrerPolicy="strict-origin-when-cross-origin"
                        />
                    )}
                    {status === 'loading' && (
                        <div className={loadingCover}>
                            <Spinner
                                size="medium"
                                labelPosition="below"
                                label={loc(loadingRewardsText)}
                            />
                        </div>
                    )}
                </div>
            )}
        </DrawerPane>
    );
},
'RewardsPane');

export default defaultExport;
